import { type UseInfiniteQueryResult, useInfiniteQuery } from "react-query"

import type { AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import type { PaginatedActualDecisionList } from "../../../types/generated/api/PaginatedActualDecisionList"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"

type Params = {
  projectId: string
  predictionId: string
  enabled?: boolean
}

/**
 * Custom hook for fetching actual decision history for a specific prediction.
 * Uses infinite query to load paginated actual decision records.
 *
 * @param params - Configuration object containing projectId, predictionId, and enabled flag
 * @param params.projectId - The UUID of the project
 * @param params.predictionId - The UUID of the prediction to fetch history for
 * @param params.enabled - Whether the query should be enabled (defaults to true)
 * @returns Infinite query result containing paginated actual decision history
 */
export function useActualDecisionHistory({
  projectId,
  predictionId,
  enabled = true,
}: Params): UseInfiniteQueryResult<AxiosResponse<PaginatedActualDecisionList>> {
  return useInfiniteQuery<AxiosResponse<PaginatedActualDecisionList>>({
    queryKey: ["actual_decision"],
    queryFn: ({ pageParam = 1 }) =>
      KonanAPI.fetchActualDecisions({
        project_uuid: projectId,
        prediction_uuid: predictionId,
        page: pageParam as number,
        page_size: 5,
      }),
    keepPreviousData: true,
    getNextPageParam: (lastPage) =>
      lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data.next) : false,
    enabled,
    refetchOnMount: true,
  })
}
